import { useEffect } from "react";
import { API_BASE_URL } from "@/lib/api";
import { usePracticeLabStore } from "@/lib/stores/practice-lab";
import type { PracticeLabSessionResponse } from "@/types/practice-lab";

// First SSE consumer in the app. Opens the live progress stream for a lab
// session and feeds each full snapshot into the store.
//
// The server emits two named events — `lab.snapshot` and `lab.completed` — and
// BOTH carry a complete PracticeLabSessionResponse. The first frame on every
// connect is a full snapshot, so a dropped-then-reconnected stream re-hydrates
// completely (there is no Last-Event-ID replay by design). Heartbeat comment
// lines (`: ping`) are ignored by EventSource automatically, and named events
// don't trigger `onmessage`, so we listen explicitly.
export function useLabEvents(
  labId: number | null,
  sessionId: number | null
): void {
  const setSnapshot = usePracticeLabStore((s) => s.setSnapshot);
  const setConnection = usePracticeLabStore((s) => s.setConnection);

  useEffect(() => {
    if (!labId || !sessionId) return;

    const url = `${API_BASE_URL}/practice-labs/${labId}/sessions/${sessionId}/events`;
    setConnection("connecting");
    const es = new EventSource(url, { withCredentials: true });

    const onSnapshot = (event: MessageEvent<string>) => {
      try {
        setSnapshot(JSON.parse(event.data) as PracticeLabSessionResponse);
      } catch {
        // Ignore a malformed frame; the next snapshot supersedes it.
      }
    };

    es.addEventListener("lab.snapshot", onSnapshot);
    es.addEventListener("lab.completed", onSnapshot);
    es.onopen = () => setConnection("open");
    // EventSource reconnects on its own; surface the transient error state and
    // let the next `lab.snapshot` re-hydrate.
    es.onerror = () => setConnection("error");

    return () => {
      es.removeEventListener("lab.snapshot", onSnapshot);
      es.removeEventListener("lab.completed", onSnapshot);
      es.close();
    };
  }, [labId, sessionId, setSnapshot, setConnection]);
}
